import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusEffect } from 'expo-router';

export function useUnreadNotificationCount() {
  const { profile } = useAuth();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!profile) return;
    try {
      const { count: c, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .eq('is_read', false);

      if (!error && c !== null) {
        setCount(c);
      }
    } catch {
      // ignore
    }
  }, [profile?.id]);

  // Fetch on mount and when profile changes
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Re-fetch on tab focus
  useFocusEffect(
    useCallback(() => {
      fetchCount();
    }, [fetchCount])
  );

  // Subscribe to realtime changes
  useEffect(() => {
    if (!profile) return;

    const channel = supabase
      .channel('unread-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          fetchCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, fetchCount]);

  return count;
}
